import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  checkUsernameAvailability,
  findUser,
  lookupUsers,
  quickCreateAdminUser,
  replaceUserCard,
  updateUser,
  updateUserCard,
} from "../api/userApi";
import SerialPortComponent from "../components/SerialPortComponent";
import { Bounce, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Search, User, CreditCard, XCircle, RefreshCw, Loader2, Coins } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MobileCodeSelector } from "../components/Selectors/Country";

const QUICK_USER_CARDS = ["CCCCCC", "BBBBBB"];
const DEFAULT_DOB_MONTH_AND_DAY = "01-01";

const dateOfBirthFromAge = (age) => {
  const parsedAge = Number(age);
  if (!Number.isInteger(parsedAge) || parsedAge < 0 || parsedAge > 130) return null;

  return `${new Date().getFullYear() - parsedAge}-${DEFAULT_DOB_MONTH_AND_DAY}`;
};

const generateQuickUsername = (fullName) => {
  const nameParts = fullName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.replace(/[^a-zA-Z0-9]/g, ""));

  if (!nameParts.length) return "";

  const firstName = nameParts[0].toLowerCase();
  const familyName = nameParts.slice(1).join("").toLowerCase();
  const familyConsonants = familyName.replace(/[aeiou]/g, "").slice(0, 3);

  return `${firstName}${familyConsonants}`;
};

export default function ManageMember() {
    const { userData } = useAuth();
  const navigate = useNavigate();
  const canCreateQuickUser = [1, 3].includes(Number(userData?.user_type));
  const [user, setUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isQuickUserModalOpen, setIsQuickUserModalOpen] = useState(false);
  const [quickUser, setQuickUser] = useState({
    fullName: "",
    username: "",
    age: "",
    gender: "",
    countryCode: "961",
    mobile: "",
    email: "",
    selectedCard: "",
  });
  const [usernameCheck, setUsernameCheck] = useState({
    checking: false,
    valid: false,
    message: "",
    suggestion: "",
  });
  const [isCreatingQuickUser, setIsCreatingQuickUser] = useState(false);
  const searchInputRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const generatedUsername = generateQuickUsername(quickUser.fullName);
    setQuickUser((currentUser) => (
      currentUser.username === generatedUsername
        ? currentUser
        : { ...currentUser, username: generatedUsername }
    ));
  }, [quickUser.fullName]);

  useEffect(() => {
    const username = quickUser.username.trim();
    if (!isQuickUserModalOpen || !username) {
      setUsernameCheck({ checking: false, valid: false, message: "", suggestion: "" });
      return undefined;
    }

    let isCurrentRequest = true;
    const checkTimeout = setTimeout(async () => {
      setUsernameCheck({ checking: true, valid: false, message: "", suggestion: "" });

      try {
        const result = await checkUsernameAvailability(username);
        if (!isCurrentRequest) return;

        setUsernameCheck({
          checking: false,
          valid: Boolean(result?.valid),
          message: result?.valid
            ? t("usernameAvailable", { defaultValue: "Username is available." })
            : t("usernameTaken", { defaultValue: "Username is already taken." }),
          suggestion: result?.valid ? "" : result?.suggestions || "",
        });
      } catch (error) {
        if (!isCurrentRequest) return;

        setUsernameCheck({
          checking: false,
          valid: false,
          message: error.message || t("usernameCheckError", { defaultValue: "Could not check username." }),
          suggestion: "",
        });
      }
    }, 350);

    return () => {
      isCurrentRequest = false;
      clearTimeout(checkTimeout);
    };
  }, [isQuickUserModalOpen, quickUser.username, t]);

  // Search user by card ID from the reader.
  const performSearch = useCallback(async (query) => {
    const normalizedQuery = query?.trim();
    if (!normalizedQuery) return;

    setIsSearching(true);
    try {
      const foundUser = await findUser(normalizedQuery);
      if (foundUser) {
        setUser(foundUser);
        toast.success(t('userFound'), { transition: Bounce });
      } else {
        setUser(null);
        toast.error(t('userNotFound'), { transition: Bounce });
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error(t('searchError'), { transition: Bounce });
    } finally {
      setIsSearching(false);
    }
  }, [t]);

  const selectLookupUser = async (lookupUser) => {
    setIsSearching(true);
    try {
      const foundUser = await findUser(lookupUser.user_name);
      setUser(foundUser);
      setSearchText(foundUser.user_name);
      setLookupResults([]);
      toast.success(t('userFound'), { transition: Bounce });
    } catch (error) {
      console.error("Lookup selection error:", error);
      toast.error(t('searchError'), { transition: Bounce });
    } finally {
      setIsSearching(false);
    }
  };

  // Search users by username, full name, mobile, or email.
  const searchUser = useCallback(async () => {
    const normalizedQuery = searchText.trim();
    if (!normalizedQuery) return;

    setIsSearching(true);
    try {
      const users = await lookupUsers(normalizedQuery);
      setLookupResults(users);
      if (users.length === 0) {
        toast.error(t('userNotFound'), { transition: Bounce });
      }
    } catch (error) {
      console.error("Lookup error:", error);
      toast.error(t('searchError'), { transition: Bounce });
    } finally {
      setIsSearching(false);
    }
  }, [searchText, t]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      searchUser();
    }
  };

  const handleCardData = useCallback((data) => {
    if (!data) return;
    console.log("Received card data:", data);

    if (data.part4 === "0") {
      setCardData(null);
      return;
    }

    const detectedCardId = data.part1?.trim();
    if (!detectedCardId) return;

    setCardData({
      ...data,
      part1: detectedCardId,
      part2: data.part2 ?? "",
      part3: data.part3 ?? "",
      part4: data.part4 ?? "",
    });

    // Auto-find user by scanned card when no user is currently loaded.
    if (!user && !isSearching && !isProcessing) {
      setSearchText(detectedCardId);
      performSearch(detectedCardId);
    }

    toast.info(t('cardDetected'), { transition: Bounce });
  }, [isProcessing, isSearching, performSearch, t, user]);

  const addCardToUser = async () => {
    if (!user || !cardData) return;
    
    setIsProcessing(true);
    try {

      const payload = {
        user_id: user.id,
        user_card: cardData.part1, // new card number
        mediator_id: userData.id,
        action: 'New',
      };
  
      const result = await updateUserCard(payload);
      console.log(result)
      setUser(result.user);
      toast.success(t('cardAddedSuccess'), { transition: Bounce });
    } catch (error) {
      console.error("Error adding card:", error);
      toast.error(t('cardAddError'), { transition: Bounce });
    } finally {
      setIsProcessing(false);
    }
  };

  const removeCard = async () => {
    if (!user || !user.user_card) return;
    
    setIsProcessing(true);
    try {
      const payload = {
        user_card: null,
      };
      const updated = await updateUser(user.id, payload);
      setUser(updated);
      toast.success(t('cardRemovedSuccess'), { transition: Bounce });
    } catch (error) {
      console.error("Error removing card:", error);
      toast.error(t('cardRemoveError'), { transition: Bounce });
    } finally {
      setIsProcessing(false);
    }
  };

  const replaceCard = async () => {
    if (!user || !cardData) return;
    
    setIsProcessing(true);
    try {
      // const payload = {
      //   user_card: cardData.part1, // New card ID
      // };
  
      // const updated = await updateUser(user.id, payload);

      const payload = {
        user_id: user.id,
        user_card: cardData.part1, // new card number
        mediator_id: userData.id,
        action: 'Replace',
      };
  
      const result = await updateUserCard(payload);
      console.log(result)
      setUser(result.user);
      toast.success(t('cardReplacedSuccess'), { transition: Bounce });
    } catch (error) {
      console.error("Error replacing card:", error);
      toast.error(t('cardReplaceError'), { transition: Bounce });
    } finally {
      setIsProcessing(false);
    }
  };

  const clearSearch = () => {
    setSearchText("");
    setLookupResults([]);
    setUser(null);
    searchInputRef.current?.focus();
  };

  const openTokenRecharge = () => {
    if (!user) return;

    navigate("/recharge", { state: { user } });
  };

  const assignQuickCard = async (cardCode) => {
    if (!user) return;

    setIsProcessing(true);
    try {
      const result = await replaceUserCard({ user_id: user.id, user_card: cardCode });
      setUser(result.user);
      toast.success(t("quickCardAssigned", { defaultValue: `Card ${cardCode} assigned.` }), { transition: Bounce });
    } catch (error) {
      console.error("Quick card assignment error:", error);
      toast.error(error.message || t('cardAddError'), { transition: Bounce });
    } finally {
      setIsProcessing(false);
    }
  };

  const openQuickUserModal = () => {
    setQuickUser({
      fullName: "",
      username: "",
      age: "",
      gender: "",
      countryCode: "961",
      mobile: "",
      email: "",
      selectedCard: "",
    });
    setUsernameCheck({ checking: false, valid: false, message: "", suggestion: "" });
    setIsQuickUserModalOpen(true);
  };

  const closeQuickUserModal = () => {
    if (!isCreatingQuickUser) {
      setIsQuickUserModalOpen(false);
    }
  };

  const applyUsernameSuggestion = () => {
    if (!usernameCheck.suggestion) return;

    setQuickUser((currentUser) => ({
      ...currentUser,
      username: usernameCheck.suggestion,
    }));
  };

  const createQuickUser = async (event) => {
    event.preventDefault();

    const dateOfBirth = dateOfBirthFromAge(quickUser.age);
    if (!quickUser.fullName.trim() || !quickUser.username || !quickUser.gender || !dateOfBirth) {
      toast.error(t("quickUserRequired", { defaultValue: "Enter a full name, age, and choose gender." }), { transition: Bounce });
      return;
    }

    if (!usernameCheck.valid || usernameCheck.checking) {
      toast.error(t("quickUserUsernameRequired", { defaultValue: "Choose an available username before creating the user." }), { transition: Bounce });
      return;
    }

    setIsCreatingQuickUser(true);
    try {
      const createdResponse = await quickCreateAdminUser({
        user_name: quickUser.username,
        full_name: quickUser.fullName.trim(),
        dob: dateOfBirth,
        gender: Number(quickUser.gender),
        country_code: quickUser.countryCode,
        user_mobile: quickUser.mobile.trim() || null,
        user_email: quickUser.email.trim() || null,
      });
      const createdUser = createdResponse.user || createdResponse;

      if (!createdUser?.id) {
        throw new Error(t("quickUserCreationFailed", { defaultValue: "Quick user creation did not return a user ID." }));
      }

      setUser(createdUser);
      setSearchText(createdUser.user_name || quickUser.username);
      setIsQuickUserModalOpen(false);
      toast.success(
        t("quickUserCreated", { defaultValue: "Quick user created. Default password: 123456789" }),
        { transition: Bounce }
      );
    } catch (error) {
      console.error("Error creating quick user:", error);
      toast.error(error.message || t("quickUserCreateError", { defaultValue: "Could not create quick user." }), { transition: Bounce });
    } finally {
      setIsCreatingQuickUser(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 mb-9">
    
      
      {/* Card Reader Component */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800">
          {t('manageSubscriptions')}
        </h1>
        <div className="flex items-center gap-3">
          {canCreateQuickUser && (
            <button
              type="button"
              onClick={openQuickUserModal}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
            >
              <User size={16} />
              {t("createQuickUser", { defaultValue: "Create Quick User" })}
            </button>
          )}
          <SerialPortComponent onCardData={handleCardData} />
        </div>
      </div>
      
      {/* Search Section */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Search size={20} className="mr-2" /> {t('findUser')}
        </h2>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              ref={searchInputRef}
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('searchPlaceholder')}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
            />
            <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
            {searchText && (
              <button 
                onClick={clearSearch}
                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
              >
                <XCircle size={18} />
              </button>
            )}
          </div>
          <button
            onClick={searchUser}
            disabled={!searchText.trim() || isSearching}
            className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2"
          >
            {isSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
            {t('search')}
          </button>
        </div>
        
        <p className="text-sm text-gray-500 mt-2">
          {t('searchInstructions', { defaultValue: 'Search by username, full name, mobile, or email.' })}
        </p>
        {lookupResults.length > 0 && (
          <div className="mt-3 overflow-hidden rounded-md border border-gray-200">
            {lookupResults.map((lookupUser) => (
              <button
                key={lookupUser.id}
                type="button"
                onClick={() => selectLookupUser(lookupUser)}
                className="flex w-full items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-blue-50"
              >
                <span>
                  <span className="block font-medium text-gray-900">{lookupUser.full_name || lookupUser.user_name}</span>
                  <span className="block text-sm text-gray-500">@{lookupUser.user_name}</span>
                </span>
                <span className="text-right text-sm text-gray-500">{lookupUser.user_mobile || lookupUser.user_email || ''}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* User Profile Section */}
      {user && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xl font-semibold flex items-center">
              <User size={20} className="mr-2" /> {t('userProfile')}
            </h2>
            <button
              type="button"
              onClick={openTokenRecharge}
              className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700"
            >
              <Coins size={16} />
              {t("rechargeTokens", { defaultValue: "Recharge Tokens" })}
            </button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-3">
              <div>
                <span className="text-gray-500 text-sm">{t('fullName')}</span>
                <p className="font-medium">{user.full_name}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">{t('username')}</span>
                <p className="font-medium">{user.user_name}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-gray-500 text-sm">{t('email')}</span>
                <p className="font-medium">{user.user_email}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">{t('membershipCard')}</span>
                <p className="font-medium">
                  {user.user_card ? 
                    <span className="flex items-center gap-1">
                      <CreditCard size={16} className="text-green-600" />
                      {user.user_card}
                    </span> : 
                    <span className="text-gray-400">{t('noCardAssigned')}</span>
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Card Management Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-3">{t('cardManagement')}</h3>
            
            <div className="flex flex-wrap gap-3">
              {!user.user_card && cardData && (
                <button
                  onClick={addCardToUser}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300 flex items-center gap-2"
                >
                  {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {t('addCard')}
                </button>
              )}
              
              {user.user_card && (
                <>
                  <button
                    onClick={removeCard}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300 flex items-center gap-2"
                  >
                    {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                    {t('removeCard')}
                  </button>
                  
                  {cardData && (
                    <button
                      onClick={replaceCard}
                      disabled={isProcessing}
                      className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-amber-300 flex items-center gap-2"
                    >
                      {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                      {t('replaceCard')}
                    </button>
                  )}
                </>
              )}
              
              {!cardData && (
                <p className="text-sm text-gray-500 italic flex items-center gap-1">
                  <CreditCard size={16} />
                  {t('insertCardMessage')}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 border-t pt-4">
            <h3 className="mb-3 font-medium">{t("quickActions", { defaultValue: "Quick Actions" })}</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {QUICK_USER_CARDS.map((cardCode, index) => (
                <button
                  key={cardCode}
                  type="button"
                  onClick={() => assignQuickCard(cardCode)}
                  disabled={isProcessing}
                  className="rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3 text-left hover:border-indigo-400 hover:bg-indigo-100 disabled:opacity-50"
                >
                  <span className="block text-xs font-medium uppercase text-indigo-700">Card {index + 1}</span>
                  <span className="block text-2xl font-bold tracking-widest text-indigo-950">{cardCode}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Card Status */}
      {cardData && (
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500">
          <div className="flex items-start gap-3">
            <CreditCard size={20} className="text-blue-500 mt-1" />
            <div>
              <h3 className="font-medium">{t('cardDetected')}</h3>
              <p className="text-sm text-gray-600">{t('cardId')}: {cardData.part1}</p>
            </div>
          </div>
        </div>
      )}

      {isQuickUserModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-user-modal-title"
        >
          <form onSubmit={createQuickUser} className="w-full max-w-xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 id="quick-user-modal-title" className="text-xl font-semibold text-gray-900">
                  {t("createQuickUser", { defaultValue: "Create Quick User" })}
                </h2>
                <p className="text-sm text-gray-500">
                  {t("quickUserPassword", { defaultValue: "Default password: 123456789" })}
                </p>
              </div>
              <button
                type="button"
                onClick={closeQuickUserModal}
                disabled={isCreatingQuickUser}
                className="text-gray-500 hover:text-gray-800 disabled:opacity-50"
                aria-label={t("close", { defaultValue: "Close" })}
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div>
                <label htmlFor="quick-user-full-name" className="mb-1 block text-sm font-medium text-gray-700">
                  {t("fullName")} *
                </label>
                <input
                  id="quick-user-full-name"
                  type="text"
                  value={quickUser.fullName}
                  onChange={(event) => setQuickUser((currentUser) => ({ ...currentUser, fullName: event.target.value }))}
                  placeholder={t("quickUserFullNamePlaceholder", { defaultValue: "First name and family name" })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="quick-user-username" className="mb-1 block text-sm font-medium text-gray-700">
                  {t("username")}
                </label>
                <input
                  id="quick-user-username"
                  type="text"
                  value={quickUser.username}
                  readOnly
                  className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700"
                />
                {usernameCheck.checking && (
                  <p className="mt-1 text-xs text-gray-500">{t("checkingUsername", { defaultValue: "Checking username..." })}</p>
                )}
                {!usernameCheck.checking && usernameCheck.message && (
                  <p className={`mt-1 text-xs ${usernameCheck.valid ? "text-green-600" : "text-red-600"}`}>
                    {usernameCheck.message}
                  </p>
                )}
                {usernameCheck.suggestion && (
                  <button
                    type="button"
                    onClick={applyUsernameSuggestion}
                    className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    {t("useSuggestion", { defaultValue: `Use suggestion: ${usernameCheck.suggestion}` })}
                  </button>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="quick-user-age" className="mb-1 block text-sm font-medium text-gray-700">
                    {t("age", { defaultValue: "Age" })} *
                  </label>
                  <input
                    id="quick-user-age"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="130"
                    step="1"
                    value={quickUser.age}
                    onChange={(event) => setQuickUser((currentUser) => ({
                      ...currentUser,
                      age: event.target.value.replace(/\D/g, ""),
                    }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t("quickUserDobHint", { defaultValue: "Date of birth is saved as January 1 of the calculated year." })}
                  </p>
                </div>
                <div>
                  <label htmlFor="quick-user-gender" className="mb-1 block text-sm font-medium text-gray-700">
                    {t("selectGender")} *
                  </label>
                  <select
                    id="quick-user-gender"
                    value={quickUser.gender}
                    onChange={(event) => setQuickUser((currentUser) => ({ ...currentUser, gender: event.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">{t("selectGender")}</option>
                    <option value="1">{t("male")}</option>
                    <option value="2">{t("female")}</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t("countryCode", { defaultValue: "Country Code" })}
                  </label>
                  <MobileCodeSelector
                    value={quickUser.countryCode}
                    onChange={(countryCode) => setQuickUser((currentUser) => ({ ...currentUser, countryCode }))}
                  />
                </div>
                <div>
                  <label htmlFor="quick-user-mobile" className="mb-1 block text-sm font-medium text-gray-700">
                    {t("mobile")}
                  </label>
                  <input
                    id="quick-user-mobile"
                    type="tel"
                    inputMode="numeric"
                    value={quickUser.mobile}
                    onChange={(event) => setQuickUser((currentUser) => ({
                      ...currentUser,
                      mobile: event.target.value.replace(/\D/g, ""),
                    }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="quick-user-email" className="mb-1 block text-sm font-medium text-gray-700">
                  {t("email")}
                </label>
                <input
                  id="quick-user-email"
                  type="email"
                  value={quickUser.email}
                  onChange={(event) => setQuickUser((currentUser) => ({ ...currentUser, email: event.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

            </div>

            <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={closeQuickUserModal}
                disabled={isCreatingQuickUser}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                disabled={isCreatingQuickUser || usernameCheck.checking || !usernameCheck.valid}
                className="flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:bg-indigo-300"
              >
                {isCreatingQuickUser && <Loader2 size={16} className="animate-spin" />}
                {t("createQuickUser", { defaultValue: "Create Quick User" })}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}